/**
 * "Register" tapped while signed out.
 *
 * A MODAL rather than an inline panel, matching StartTournamentModal: the
 * decision interrupts what they were doing, it has two exits, and global.css
 * already carries the veil/head/body/foot classes for exactly this shape. The
 * Escape and veil-click behaviour is copied from there too.
 *
 * ACCOUNT FIRST, GUEST BENEATH IT. That order is the recommendation — an
 * account keeps their stats — but guest is one click away and never behind a
 * disclosure, because plenty of people genuinely just want to play tonight.
 *
 * Nothing in here is wired to a trigger yet. TournamentRegister.jsx is still a
 * placeholder (Stage 5); this is built to drop into it, which is why every
 * identifier it needs arrives as a prop rather than being read from the URL.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profilePathFor } from '../../lib/signupReturn.js';
import SignUpForm from './SignUpForm.jsx';
import GuestRegisterForm from './GuestRegisterForm.jsx';

export default function AccountPrompt({
  venueSlug, venueId, tournamentId, tournamentLabel, onClose, onGuestRegistered,
}) {
  const [mode, setMode] = useState('account');
  const panel = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { panel.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const context = { venueSlug, tournamentId };

  return (
    <div
      className="hd-modal-veil"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="hd-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hd-account-prompt-title"
        tabIndex={-1}
        ref={panel}
      >
        <div className="hd-modal-head">
          <h2
            className="hd-section-title"
            id="hd-account-prompt-title"
            style={{ margin: 0 }}
          >
            {mode === 'account' ? 'Create an account' : 'Register as a guest'}
          </h2>
          <button
            type="button"
            className="hd-btn hd-btn-quiet hd-btn-sm"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>

        <div className="hd-modal-body hd-stack">
          {mode === 'account' ? (
            <>
              <p className="hd-hint" style={{ margin: 0 }}>
                An account keeps your averages, 180s and season standings across
                every night you play
                {tournamentLabel ? ` — starting with ${tournamentLabel}.` : '.'}
              </p>

              <SignUpForm
                context={context}
                onSuccess={() => {
                  onClose();
                  navigate(profilePathFor(context), { replace: true });
                }}
              />
            </>
          ) : (
            <GuestRegisterForm
              venueId={venueId}
              tournamentId={tournamentId}
              onDone={onGuestRegistered}
            />
          )}
        </div>

        <div className="hd-modal-foot">
          {mode === 'account' ? (
            <button
              type="button"
              className="hd-btn hd-btn-quiet"
              onClick={() => setMode('guest')}
            >
              Register as guest instead
            </button>
          ) : (
            <button
              type="button"
              className="hd-btn hd-btn-quiet"
              onClick={() => setMode('account')}
            >
              Create an account instead
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
